import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Squares2X2Icon,
  UserGroupIcon,
  ShoppingBagIcon,
  CubeIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ArchiveBoxIcon,
  DocumentChartBarIcon,
  BanknotesIcon,
  ReceiptRefundIcon,
  LinkIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  CodeBracketIcon,
  BoltIcon,
  TruckIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  ClockIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UsersIcon,
  SparklesIcon,
  RectangleStackIcon,
  ClipboardDocumentListIcon,
  CursorArrowRaysIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../utils/cn';
import { useAuthContext } from '../context/AuthContext';
import XpertifyLogo from './XpertifyLogo';

// Sidebar tree per spec v2 final ordering. Single-level subItems only.
const navigation: any[] = [
  { name: 'Dashboard', icon: Squares2X2Icon, path: '/dashboard', accessKey: 'dashboard' },

  { name: 'Sales',  icon: BanknotesIcon, path: '/sales' },
  { name: 'Orders', icon: ShoppingBagIcon, path: '/orders', accessKey: 'orders' },

  {
    name: 'Products', icon: CubeIcon, path: '/products', navigateOnClick: true,
    subItems: [
      { name: 'Product Catalog',   icon: ClipboardDocumentListIcon, path: '/products/catalog',    accessKey: 'products' },
      { name: 'Product Categories', icon: FolderIcon,               path: '/products/categories', accessKey: 'products' },
    ]
  },

  {
    name: 'Inventory', icon: ArchiveBoxIcon, path: '/inventory', navigateOnClick: true,
    subItems: [
      { name: 'Stock Management', icon: ArchiveBoxIcon,             path: '/inventory/stock-management' },
      { name: 'Stock Adjustment', icon: ClipboardDocumentListIcon, path: '/inventory/stock-adjustment' },
    ]
  },

  { name: 'Leads', icon: UsersIcon, path: '/leads' },

  {
    name: 'Web Architect', icon: GlobeAltIcon, path: '/web',
    subItems: [
      { name: 'Web Analytics',      icon: ChartBarIcon,         path: '/web/analytics' },
      { name: 'AI Website Builder', icon: SparklesIcon,         path: '/web/builder' },
      { name: 'Pages Library',      icon: RectangleStackIcon,   path: '/web/pages' },
      { name: 'Forms & Tracking',   icon: CursorArrowRaysIcon,  path: '/web/forms' },
      { name: 'Domains Manager',    icon: GlobeAltIcon,         path: '/web/domains' },
      { name: 'SEOptimizer',        icon: MagnifyingGlassIcon,  path: '/web/seo' },
    ]
  },

  {
    name: 'Finance', icon: CurrencyDollarIcon, path: '/finance', navigateOnClick: true,
    subItems: [
      { name: 'Cash Flow',     icon: ArrowTrendingUpIcon,  path: '/finance/cashflow' },
      { name: 'Profit & Loss', icon: DocumentChartBarIcon, path: '/finance/pnl' },
    ]
  },

  {
    name: 'Reports', icon: DocumentChartBarIcon, path: '/report', accessKey: 'report',
    subItems: [
      { name: 'Report Center',    icon: DocumentChartBarIcon, path: '/report/center', accessKey: 'report' },
      { name: 'Audit Log Center', icon: DocumentChartBarIcon, path: '/report/audit',  accessKey: 'report' },
    ]
  },

  {
    name: 'Integration', icon: LinkIcon, path: '/integration', accessKey: 'integration',
    subItems: [
      // Spec v2 ordering — Webhook / API Keys / Courier surfaced first.
      { name: 'Webhook',         icon: BoltIcon,                  path: '/integration/webhook',     accessKey: 'integration' },
      { name: 'API Keys',        icon: CodeBracketIcon,           path: '/integration/api',         accessKey: 'integration' },
      { name: 'Courier',         icon: TruckIcon,                 path: '/integration/courier',     accessKey: 'integration' },
      { name: 'Payment Gateway', icon: CreditCardIcon,            path: '/integration/payment' },
      { name: 'Email',           icon: EnvelopeIcon,              path: '/integration/email',       accessKey: 'integration' },
      { name: 'WAHA (WhatsApp)', icon: ChatBubbleLeftRightIcon,   path: '/integration/waha',        accessKey: 'integration' },
      { name: 'WooCommerce',     icon: GlobeAltIcon,              path: '/integration/woocommerce', accessKey: 'integration' },
      { name: 'Shopee',          icon: ShoppingBagIcon,           path: '/integration/shopee',      accessKey: 'integration' },
      { name: 'TikTok',          icon: DevicePhoneMobileIcon,     path: '/integration/tiktok',      accessKey: 'integration' },
      { name: 'Ladipage',        icon: GlobeAltIcon,              path: '/integration/ladipage',    accessKey: 'integration' },
      { name: 'Marketplace',     icon: ShoppingBagIcon,           path: '/integration/marketplace' },
      { name: 'Web Builder',     icon: GlobeAltIcon,              path: '/integration/webbuilder' },
    ]
  },

  {
    name: 'System', icon: WrenchScrewdriverIcon, path: '/system',
    subItems: [
      { name: 'Settings',               icon: Cog6ToothIcon,             path: '/settings',          accessKey: 'settings' },
      { name: 'Access Management',      icon: ShieldCheckIcon,           path: '/access-management' },
      { name: 'Billing & Subscription', icon: ReceiptRefundIcon,         path: '/billing' },
      { name: 'Help Center',            icon: QuestionMarkCircleIcon,    path: '/help-support',     accessKey: 'help_support' },
    ]
  },

  { name: 'Logout', icon: ArrowRightOnRectangleIcon, path: '/logout', isLogout: true },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, signOut } = useAuthContext();
  const [expanded, setExpanded] = useState(true);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Function to check access
  const hasAccess = (accessKey?: string) => {
    if (!accessKey) return true;
    if (!userProfile?.access) return false;

    let checkKey = accessKey;
    if (accessKey === 'staff_management' && userProfile.access.includes('users')) return true;
    if (accessKey === 'documentation' && userProfile.access.includes('documents')) return true;

    return userProfile.access.includes(checkKey);
  };

  // Function to check if main menu should be highlighted
  const isMainMenuActive = (item: any) => {
    if (!item.subItems) return false;
    return location.pathname.startsWith(item.path) && location.pathname !== item.path;
  };

  // Auto-expand menu when on sub-routes
  useEffect(() => {
    const currentPath = location.pathname;
    const shouldExpand: string[] = [];

    navigation.forEach((item: any) => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some((subItem: any) => subItem.path === currentPath);
        if (hasActiveSubItem) {
          shouldExpand.push(item.name);
        }
      }
    });

    setExpandedItems(prev => {
      const combined = [...prev, ...shouldExpand];
      const newExpanded = Array.from(new Set(combined));
      return newExpanded;
    });
  }, [location.pathname]);

  const toggleSidebar = () => {
    setExpanded(!expanded);
  };

  const toggleSubMenu = (name: string, accessKey?: string) => {
    if (!hasAccess(accessKey)) return;
    setExpandedItems(prev =>
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name]
    );
  };

  // Auto-collapse sidebar on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setExpanded(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className={cn(
        'flex flex-col transition-all duration-300 relative',
        'bg-white dark:bg-[#1e293b]',
        'border-r border-gray-200 dark:border-gray-700/50',
        'sticky top-0 h-screen z-40',
        'shadow-sm dark:shadow-black/10',
        expanded ? 'w-[280px]' : 'w-[64px]'
      )
      }
    >
      {/* Logo/Brand Section */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
        {expanded ? (
          <div className="flex items-center gap-2">
            <XpertifyLogo size={28} />
            <span className="font-semibold text-gray-900 dark:text-white">Xpertify</span>
          </div>
        ) : (
          <div className="mx-auto">
            <XpertifyLogo size={28} />
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <div className="absolute top-20 -right-3 z-20">
        <button
          onClick={toggleSidebar}
          className={cn(
            'w-6 h-6 rounded-full',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-700',
            'shadow-md',
            'flex items-center justify-center',
            'text-gray-600 dark:text-gray-400',
            'hover:text-primary-600 dark:hover:text-primary-400',
            'hover:border-primary-500',
            'transition-all duration-200',
            'hover:scale-110'
          )}
        >
          {expanded ? (
            <ChevronLeftIcon className="w-3.5 h-3.5" />
          ) : (
            <ChevronRightIcon className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className={cn(
        'flex-1 overflow-y-auto custom-scrollbar',
        expanded ? 'px-3 py-4' : 'px-2 py-4',
        'space-y-1'
      )}>
        {navigation.map((item: any) => {
          const isAllowed = hasAccess(item.accessKey);

          return (
            <div key={item.name} className="relative">
              {/* Main menu item */}
              {item.subItems ? (
                <button
                  onClick={() => {
                    toggleSubMenu(item.name, item.accessKey);
                    if ((item as any).navigateOnClick && item.path && isAllowed) navigate(item.path);
                  }}
                  disabled={!isAllowed}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
                    'text-sm font-medium transition-all duration-200',
                    'group relative',
                    isMainMenuActive(item)
                      ? 'bg-primary-50 dark:bg-primary-950 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                    !isAllowed && 'opacity-40 cursor-not-allowed',
                    !expanded && 'justify-center'
                  )}
                >
                  <item.icon className={cn(
                    'w-5 h-5 flex-shrink-0 transition-colors',
                    isMainMenuActive(item)
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                  )} />

                  {expanded && (
                    <>
                      <span className="flex-1 text-left truncate">{item.name}</span>
                      <ChevronDownIcon
                        className={cn(
                          'w-4 h-4 transition-transform duration-200',
                          expandedItems.includes(item.name) && 'rotate-180'
                        )}
                      />
                    </>
                  )}

                  {/* Tooltip for collapsed state */}
                  {!expanded && isAllowed && (
                    <span className={cn(
                      'absolute left-full ml-2 px-3 py-2 rounded-lg',
                      'bg-gray-900 dark:bg-gray-700 text-white text-sm font-medium',
                      'whitespace-nowrap shadow-lg',
                      'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
                      'transition-all duration-200',
                      'pointer-events-none z-50'
                    )}>
                      {item.name}
                    </span>
                  )}
                </button>
              ) : item.isLogout ? (
                <button
                  onClick={async () => {
                    await signOut();
                    localStorage.removeItem('isAuthenticated');
                    navigate('/login');
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
                    'text-sm font-medium transition-all duration-200',
                    'text-error-600 dark:text-error-400',
                    'hover:bg-error-50 dark:hover:bg-error-950',
                    'group relative',
                    !expanded && 'justify-center'
                  )}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {expanded && <span className="flex-1 text-left">{item.name}</span>}

                  {!expanded && (
                    <span className={cn(
                      'absolute left-full ml-2 px-3 py-2 rounded-lg',
                      'bg-gray-900 dark:bg-gray-700 text-white text-sm font-medium',
                      'whitespace-nowrap shadow-lg',
                      'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
                      'transition-all duration-200',
                      'pointer-events-none z-50'
                    )}>
                      {item.name}
                    </span>
                  )}
                </button>
              ) : (
                <NavLink
                  to={isAllowed ? item.path : '#'}
                  onClick={(e) => !isAllowed && e.preventDefault()}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg',
                    'text-sm font-medium transition-all duration-200',
                    'group relative',
                    isActive && isAllowed
                      ? 'bg-primary-50 dark:bg-primary-950 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                    !isAllowed && 'opacity-40 cursor-not-allowed pointer-events-none',
                    !expanded && 'justify-center'
                  )}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className={cn(
                        'w-5 h-5 flex-shrink-0 transition-colors',
                        isActive && isAllowed
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                      )} />

                      {expanded && <span className="flex-1 text-left truncate">{item.name}</span>}

                      {!expanded && isAllowed && (
                        <span className={cn(
                          'absolute left-full ml-2 px-3 py-2 rounded-lg',
                          'bg-gray-900 dark:bg-gray-700 text-white text-sm font-medium',
                          'whitespace-nowrap shadow-lg',
                          'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
                          'transition-all duration-200',
                          'pointer-events-none z-50'
                        )}>
                          {item.name}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              )}

              {/* Sub menu items */}
              {item.subItems && expandedItems.includes(item.name) && expanded && isAllowed && (
                <div className="mt-1 ml-4 pl-4 border-l border-gray-200 dark:border-gray-700 space-y-1">
                  {item.subItems.map((subItem: any) => {
                    const isSubAllowed = hasAccess(subItem.accessKey);

                    return (
                      <NavLink
                        key={subItem.name}
                        to={isSubAllowed ? subItem.path : '#'}
                        onClick={(e) => !isSubAllowed && e.preventDefault()}
                        className={({ isActive }) => cn(
                          'flex items-center gap-2 px-3 py-2 rounded-lg',
                          'text-sm font-medium transition-all duration-200',
                          isActive && isSubAllowed
                            ? 'bg-primary-50 dark:bg-primary-950 text-primary-600 dark:text-primary-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800',
                          !isSubAllowed && 'opacity-40 cursor-not-allowed pointer-events-none'
                        )}
                      >
                        {({ isActive }) => (
                          <>
                            <subItem.icon className={cn(
                              'w-4 h-4 flex-shrink-0',
                              isActive && isSubAllowed
                                ? 'text-primary-600 dark:text-primary-400'
                                : 'text-gray-400 dark:text-gray-500'
                            )} />
                            <span className="truncate">{subItem.name}</span>
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Brand badge — pinned to bottom of sidebar */}
      <div className={cn(
        'border-t border-gray-200 dark:border-gray-800 p-3',
        !expanded && 'px-2'
      )}>
        {expanded ? (
          <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="h-8 w-8 rounded-lg bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
              <XpertifyLogo size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-gray-900 dark:text-white leading-tight">Xpertify</p>
              <p className="text-[10px] text-gray-500 leading-tight truncate">All-in-One Business Management Platform</p>
            </div>
          </div>
        ) : (
          <div
            className="h-8 w-8 mx-auto rounded-lg bg-orange-500 text-white flex items-center justify-center"
            title="Xpertify — All-in-One Business Management Platform"
          >
            <XpertifyLogo size={18} />
          </div>
        )}
      </div>
    </div>
  );
}